import { useCallback, useMemo, useRef, } from 'react';
/**
 * アコーディオンのアニメーションを制御するカスタムフック
 * @description details/summary の HTML 構造に依存しています
 */
export const useAccordionDetails = (options) => {
    const { contentRef, detailsRef, duration = 300 } = options;
    /**
     * 開閉のアニメーション実行中か判定するための値
     */
    const isDuringAnimationRef = useRef(false);
    /**
     * アニメーションの時間とイージング
     */
    const animTiming = useMemo(() => ({
        duration,
        easing: 'ease-out',
    }), [duration]);
    /**
     * アコーディオンを閉じるときのキーフレーム
     */
    const closingAnimKeyframes = useCallback((content) => [
        {
            height: `${content.offsetHeight}px`,
            opacity: 1,
        },
        {
            height: 0,
            opacity: 0,
        },
    ], []);
    /**
     * アコーディオンを開くときのキーフレーム
     */
    const openingAnimKeyframes = useCallback((content) => [
        {
            height: 0,
            opacity: 0,
        },
        {
            height: `${content.offsetHeight}px`,
            opacity: 1,
        },
    ], []);
    /**
     * summary がクリックされたときに開閉を切り替え + アニメーションを実行する
     */
    const onClickSummary = useCallback((e) => {
        const detailsElement = detailsRef.current;
        const contentElement = contentRef.current;
        if (!detailsElement || !contentElement) {
            return;
        }
        // デフォルトの挙動を無効化
        e.preventDefault();
        // アニメーション中だったら何もしない（連打防止用）
        if (isDuringAnimationRef.current) {
            return;
        }
        // detailsのopen属性を判定
        if (detailsElement.open) {
            // アコーディオンを閉じるときの処理
            // アニメーションを実行
            const closingAnim = contentElement.animate(closingAnimKeyframes(contentElement), animTiming);
            // アニメーション実行中の判定フラグを true にする
            isDuringAnimationRef.current = true;
            // アニメーションの完了後に
            closingAnim.onfinish = () => {
                // open属性を取り除く
                detailsElement.removeAttribute('open');
                // アニメーション実行中の判定フラグを false にする
                isDuringAnimationRef.current = false;
            };
        }
        else {
            // アコーディオンを開くときの処理
            // open属性を付与
            detailsElement.setAttribute('open', 'true');
            // アニメーションを実行
            const openingAnim = contentElement.animate(openingAnimKeyframes(contentElement), animTiming);
            // アニメーション実行中の判定フラグを true にする
            isDuringAnimationRef.current = true;
            // アニメーション完了後にアニメーション実行中の判定フラグを false にする
            openingAnim.onfinish = () => {
                isDuringAnimationRef.current = false;
            };
        }
    }, [
        detailsRef,
        contentRef,
        closingAnimKeyframes,
        openingAnimKeyframes,
        animTiming,
    ]);
    return { onClickSummary };
};
